import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, ViewChild, AfterViewInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ClosestAssetRefRelatedDocumentsRequest,
  DistributionList,
  DocumentSelectionInfo,
  MassRecipientType
} from 'src/app/modules/communications/store/communications/communications.model';
import { DataTableServerComponent } from 'src/app/modules/core/components/data-table-server/data-table.component';
import { GetRowIdParams, IServerSideDatasource, ValueFormatterParams } from 'ag-grid-enterprise';
import { GridHelperService } from 'src/app/modules/core/services/grid-helper.service';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { DocumentQueryParams, FilterGroup, FilterGroupType, RuleOperator } from 'src/app/modules/core/store/models';
import { UserContextService } from 'src/app/modules/core/services/user-context.service';
import { ClosestAssetFilterDto } from 'src/app/modules/cop/store/impacted-assets';
import { TableColumnsService } from 'src/app/modules/cop/services/table/table-columns-service';
import { ImpactedAssetsApiService } from 'src/app/modules/cop/services/impacted-assets-api.service';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { DataApiService } from 'src/app/modules/management/store/data/data-api.service';
import { DatasetApiService } from 'src/app/modules/core/services/dataset-api.service';
import { Subject } from 'rxjs';

@Component({
    selector: 'topo-comms-recipient-details-list',
    templateUrl: './recipient-details-list.component.html',
    styleUrls: ['./recipient-details-list.component.scss'],
    standalone: false
})
export class RecipientDetailsListComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() set activeContext(value: { layer?: Layer; assetsSelectionFilter?: ClosestAssetFilterDto; distributionList?: DistributionList }) {
    this.activatedContext = value;

    if (!!this.activatedContext) {
      setTimeout(() => {
        this.dataTable?.groupRows(false);
        this.dataTable?.resetFilter();
      }, 200);

      this.loadData();
    }
  }

  activatedContext: {
    layer?: Layer;
    assetsSelectionFilter?: ClosestAssetFilterDto;
    distributionList?: DistributionList;
  };

  @Output() recipientsDetailsClosed = new EventEmitter();

  @ViewChild('table') dataTable: DataTableServerComponent;

  dialogLabel: string;
  headerLabel: string;
  headerIcon: string;
  datasource: any;
  columDefs: any[] = [];
  skip = 0;
  take = 10;
  totalCount: number = 0;
  searchFilterGroup: FilterGroup;
  dataset: Dataset;

  destroy$: Subject<void> = new Subject<void>();

  tableWidth = '70vw';
  tableHeight = '60vh';

  constructor(
    private gridHelper: GridHelperService,
    private translateService: TranslateService,
    private tableColumnsService: TableColumnsService,
    private dataApiService: DataApiService,
    private datasetApiService: DatasetApiService,
    private userContextService: UserContextService,
    private impactedAssetsApiService: ImpactedAssetsApiService
  ) {}

  ngOnInit(): void {
    this.datasource = this.getServerSideDatasource();

    this.dialogLabel = this.translateService.instant('COMMUNICATIONS.RECIPIENT-DETAILS');
  }

  ngAfterViewInit() {
    this.dataTable?.groupRows(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getServerSideDatasource(): IServerSideDatasource {
    return {
      getRows: params => {
        if (!!!this.dataset) {
          params.success({
            rowData: [],
            rowCount: 0
          });

          return;
        }
        const translatedParams = this.gridHelper.gridParamsToRequestParams(params.request);

        let columnFilter = this.tableColumnsService.getColumnFilterFromGrid(params?.request.filterModel);

        var documentQuery = {
          pagination: {
            skip: translatedParams.skip,
            take: translatedParams.take
          },
          sort: translatedParams.sort,
          filterGroup: columnFilter
        } as DocumentQueryParams;

        if (!!this.activatedContext.assetsSelectionFilter) {
          var filterGroups = [];

          if (!!documentQuery.filterGroup) {
            filterGroups.push(filterGroups);
          }
          const layerData = this.activatedContext.assetsSelectionFilter?.layerStates?.find(
            l => l.layerUId === this.activatedContext.layer.uId
          );

          if (!!layerData && !!layerData?.queryMatchRuleDto?.filterGroup) {
            filterGroups.push(layerData?.queryMatchRuleDto?.filterGroup);
          }

          const combinedFilterGroup: FilterGroup = {
            filterGroups: filterGroups,
            filters: [],
            groupType: FilterGroupType.And
          };

          const assetQuery: ClosestAssetFilterDto = {
            ...this.activatedContext.assetsSelectionFilter,
            layerStates: [
              {
                layerUId: this.activatedContext.layer.uId,
                datasetUId: this.dataset.uId,
                queryMatchRuleDto: {
                  filterGroup: combinedFilterGroup,
                  fields: documentQuery.fields,
                  pagination: documentQuery.pagination,
                  sort: documentQuery.sort,
                  calcFields: documentQuery.calcFields
                }
              }
            ]
          };

          return this.impactedAssetsApiService.calculateClosestAssetsForLayer(assetQuery, this.activatedContext.layer.uId).subscribe(
            resp => {
              const response = this.gridHelper.queryResultToGridData(resp);
              this.totalCount = response?.rowCount;

              params.success({
                rowData: response?.rowData,
                rowCount: response?.rowCount
              });
            },
            err => params.fail()
          );
        } else if (this.activatedContext.distributionList) {
          const combinedFilterGroup: FilterGroup = {
            filterGroups: [],
            filters: [],
            groupType: FilterGroupType.And
          };
          if (!!documentQuery.filterGroup) {
            combinedFilterGroup.filterGroups.push(documentQuery.filterGroup);
          }
          if (
            !!this.activatedContext.distributionList.queryMatchRule?.root &&
            (!!this.activatedContext.distributionList.queryMatchRule?.root.filterGroups?.length ||
              !!this.activatedContext.distributionList.queryMatchRule?.root.filters?.length)
          ) {
            combinedFilterGroup.filterGroups.push(this.activatedContext.distributionList.queryMatchRule?.root);
          }

          if (!!this.searchFilterGroup) {
            combinedFilterGroup.filterGroups.push(this.searchFilterGroup);
          }

          documentQuery.filterGroup = combinedFilterGroup;

          this.dataApiService.getDocuments(this.activatedContext.distributionList.queryMatchRule?.datasetUId, documentQuery).subscribe(
            resp => {
              const response = this.gridHelper.queryResultToGridData(resp);
              this.totalCount = response?.rowCount;

              params.success({
                rowData: response?.rowData,
                rowCount: response?.rowCount
              });
            },
            err => params.fail()
          );
        }
      }
    };
  }

  onPaginate(skip: number): void {
    this.skip = skip;
  }

  onTakeChanged(take: number): void {
    this.take = take;
  }

  onSelectData(data: any): void {}

  private generateColumns(): void {
    const addDistanceCol = !!this.activatedContext.assetsSelectionFilter;
    if (!!this.dataset) {
      this.columDefs = this.tableColumnsService.getDatasetColumns(
        this.dataset,
        this.userContextService.getUserContext().organizationUId,
        addDistanceCol,
        true
      );
    }
  }

  formatTextForTable(params: ValueFormatterParams) {
    const text = params.value;
    if (text) {
      return text.replace(/(\r\n|\n|\r)/gm, '');
    }
    return text;
  }

  private loadData(): void {
    const datasetUId = !!this.activatedContext.layer
      ? this.activatedContext.layer.datasetUId
      : this.activatedContext.distributionList?.queryMatchRule?.datasetUId;
    this.datasetApiService.getDataset(datasetUId, { includeWorkflow: true }).subscribe(dataset => {
      if (!!dataset) {
        this.dataset = dataset;
        this.generateColumns();
        this.skip = 0;
        this.take = 10;
        if (!!this.activatedContext.layer) {
          this.dialogLabel = this.translateService.instant('COMMUNICATIONS.DETAIL-CLOSEST-ASSETS');
          this.headerLabel = this.activatedContext.layer?.name;
        } else {
          this.dialogLabel = this.translateService.instant('COMMUNICATIONS.DETAIL-DIST-LIST');
          this.headerLabel = this.activatedContext.distributionList?.name;
        }
        this.dataTable?.refreshGrid();
      }
    });
  }

  close(): void {
    this.recipientsDetailsClosed.emit();
  }

  cancelClick(): void {
    this.close();
  }

  onSearchFilterChanged(filterGroup: FilterGroup): void {
    this.searchFilterGroup = filterGroup;
  }

  onGridDataLoaded(): void {
    this.dataTable?.gridApi?.sizeColumnsToFit();
  }

  getRowId = (params: GetRowIdParams): string => {
    return params.data.uid;
  };
}
